// src/core/redux/services/baseApi.ts
//
// Central RTK Query API for the admin console. This is the single HTTP client:
// - one fetchBaseQuery with the platform base URL,
// - a request layer that injects the Bearer token,
// - a response layer that, on 401, refreshes the token ONCE (concurrent 401s share
//   the same refresh) and retries; on refresh failure it clears tokens, logs out,
//   and redirects to sign-in.
//
// New admin integrations should `baseApi.injectEndpoints(...)` rather than adding
// hand-rolled fetch files — they get caching, tag invalidation, and auth for free.

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import BASE_URL from '../apis/api';
import { logout, refreshSuccess } from '../slices/auth';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const stateToken = (getState() as any)?.auth?.accessToken;
    const token = stateToken || localStorage.getItem('accessToken');
    if (token) headers.set('authorization', `Bearer ${token}`);
    return headers;
  },
});

// Single-flight refresh: concurrent 401s await the same in-progress refresh.
let refreshPromise: Promise<boolean> | null = null;

// The single-flight promise is a module-level singleton, so if a refresh never
// settles it is not one request that hangs — it is every query in the app, for
// the life of the tab, with no further network activity to show for it (RTK
// Query will not restart a query it still believes is in flight). This bound is
// defensive rather than a fix for an observed production hang: the slow refresh
// that prompted it was measured in a throttled background tab, not on a real
// desktop. It costs nothing and turns a worst case from "the app is dead" into
// "you are signed out".
const REFRESH_TIMEOUT_MS = 15_000;

async function refreshOnce(
  api: Parameters<BaseQueryFn>[1],
  extraOptions: Parameters<BaseQueryFn>[2]
): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) return false;
      const res = await rawBaseQuery(
        { url: 'auth/refresh', method: 'POST', body: { refreshToken } },
        api,
        extraOptions
      );
      const data: any = (res.data as any)?.data ?? res.data;
      if (data?.accessToken) {
        localStorage.setItem('accessToken', data.accessToken);
        if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
        // Also put it in the store. prepareHeaders reads state.auth.accessToken
        // FIRST and only falls back to localStorage, so writing to storage alone
        // left the retry re-sending the same dead token: 401 → refresh → 401,
        // and every query on the page hung in `pending` for good. Found on the
        // 2026-09-09 audit-viewer verification, where a fresh sign-in left the
        // persisted state token stale and the whole admin app stopped loading.
        api.dispatch(refreshSuccess({ accessToken: data.accessToken }));
        return true;
      }
      return false;
    })();
    // Bound it, so a hung refresh degrades to a normal auth failure (sign-in)
    // rather than a silent app-wide stall.
    refreshPromise = Promise.race([
      refreshPromise,
      new Promise<boolean>((resolve) => setTimeout(() => resolve(false), REFRESH_TIMEOUT_MS)),
    ]);

    // Reset once settled so the next 401 starts a fresh refresh. Also swallow a
    // rejection here: an unhandled one would leave the singleton set forever.
    refreshPromise.catch(() => false).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    const ok = await refreshOnce(api, extraOptions);
    if (ok) {
      result = await rawBaseQuery(args, api, extraOptions); // retry with the new token
    } else {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      api.dispatch(logout());
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/signin')) {
        window.location.href = '/signin';
      }
    }
  }
  return result;
};

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Tenant',
    'TenantStores',
    'TenantWarehouses',
    'TenantUsers',
    'TenantCustomers',
    'TenantTransactions',
    'TenantPayments',
    'TenantInventories',
    'TenantPosSessions',
    'TenantBusinessEntities',
    'TenantSuppliers',
    'TenantProductSuppliers',
    // Room for later phases:
    'Role',
    'Permission',
    'Session',
    'Warehouse',
    'BusinessEntity',
    'Setting',
    'ProductSupplier',
    'B2bConnection',
    'Barcode',
    'AuditLog',
    'TenantCategory',
    'TenantCatalogProduct',
    'TenantSupplier',
    // Phase 3 operational views
    'Transaction',
    'Payment',
    'PosSession',
    'Stock',
    'InventoryMovement',
    'StockTransfer',
    'Quotation',
    'PurchaseOrder',
    // Compliance / KYC
    'Kyc',
    'KycQueue',
    // Support console (impersonation, repairs, cases)
    'Impersonation',
    'KycExemption',
    'SupportCase',
  ],
  endpoints: () => ({}),
});
